import { formatDateTime, formatTime } from '../../../utils/format'

/** "há 3 min" a partir de uma data UTC (ISO com Z). */
export function timeAgo(value) {
  if (!value) return '—'
  const ts = new Date(value).getTime()
  if (Number.isNaN(ts)) return '—'
  const diff = Math.max(0, Date.now() - ts) / 1000
  if (diff < 60) return 'agora'
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`
  return formatDateTime(value)
}

export function formatBytes(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let v = Number(bytes)
  let i = 0
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`
}


export const NETWORK_LABEL = { wifi: 'Wi-Fi', cellular: 'Celular', ethernet: 'Cabo', none: 'Sem rede' }


export const COMMAND_LABELS = {
  lock: 'Bloquear',
  reboot: 'Reiniciar',
  wipe: 'Apagar (wipe)',
  locate: 'Localizar',
  ring: 'Tocar alarme',
  message: 'Mensagem',
  screenshot: 'Captura de tela',
  'install-apk': 'Instalar APK',
  'uninstall-app': 'Desinstalar app',
  'set-app-hidden': 'Ocultar/mostrar app',
  'set-app-suspended': 'Suspender/liberar app',
  'clear-app-data': 'Limpar dados do app',
  'kiosk-start': 'Iniciar quiosque',
  'kiosk-stop': 'Parar quiosque',
  'kiosk-resume': 'Reativar quiosque',
  'mdm-resume': 'Retomar MDM',
  'sync-policy': 'Sincronizar política',
  'sync-apps': 'Sincronizar apps',
  'push-file': 'Enviar arquivo',
  'pull-file': 'Buscar arquivo',
  'update-agent': 'Atualizar agente',
}

export const COMMAND_STATUS_CLASSES = {
  Pending: 'bg-gray-100 text-gray-700',
  Sent: 'bg-blue-100 text-blue-800',
  Acked: 'bg-indigo-100 text-indigo-800',
  Done: 'bg-green-100 text-green-800',
  Failed: 'bg-red-100 text-red-800',
  Expired: 'bg-amber-100 text-amber-800',
  Canceled: 'bg-gray-100 text-gray-500',
}

export const COMMAND_STATUS_LABELS = {
  Pending: 'Pendente',
  Sent: 'Enviado',
  Acked: 'Recebido',
  Done: 'Concluído',
  Failed: 'Falhou',
  Expired: 'Expirado',
  Canceled: 'Cancelado',
}


/** Compara versões do agente por componente ("1.0.10" > "1.0.9"). */
export function compareAgentVersions(a, b) {
  const pa = String(a || '').split('.').map((x) => parseInt(x, 10) || 0)
  const pb = String(b || '').split('.').map((x) => parseInt(x, 10) || 0)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0)
    if (d !== 0) return d
  }
  return 0
}

/** Situação da versão do agente (AgentUpdateState do servidor). */
export const AGENT_UPDATE_STATE = {
  none: { label: 'Sem versão-alvo', className: 'bg-gray-100 text-gray-600' },
  upToDate: { label: 'Na versão-alvo', className: 'bg-green-100 text-green-800' },
  updating: { label: 'Atualizando', className: 'bg-amber-100 text-amber-800' },
  failing: { label: 'Falha ao atualizar', className: 'bg-red-100 text-red-800' },
  ahead: { label: 'Acima da versão-alvo', className: 'bg-gray-100 text-gray-600' },
}

/** "do grupo X" / "da raiz" / "fixada neste tablet" para a versão-alvo de um item da frota. */
export function agentTargetSourceLabel(item) {
  if (item?.agentTargetSource === 'device') return 'fixada neste tablet'
  if (item?.agentTargetSource === 'group') return `do grupo ${item.agentTargetGroupName || ''}`.trim()
  if (item?.agentTargetSource === 'tenant') return 'de todos os tablets (raiz)'
  return ''
}

/** "do grupo X" / "de todos os tablets (raiz)" / "padrão" para a política herdada (MdmStateDto). */
export function policySourceLabel(state) {
  if (state?.policySource === 'group') return `do grupo ${state.policyGroupName || ''}`.trim()
  if (state?.policySource === 'tenant') return 'de todos os tablets (raiz)'
  return 'padrão do sistema'
}

/** Mensagem de erro legível de uma resposta axios. */
export function errorMessage(e, fallback = 'Falha na operação.') {
  return e?.response?.data?.message || e?.message || fallback
}

/** Baixa um Blob como arquivo no navegador. */
export function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName || 'arquivo'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const sameLocalDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

/** Data UTC (ISO com Z) no futuro? */
export function isFuture(value) {
  if (!value) return false
  const ts = new Date(value).getTime()
  return !Number.isNaN(ts) && ts > Date.now()
}

/** "15:10" se for hoje (hora local do navegador), senão data e hora. */
export function formatUntil(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return sameLocalDay(d, new Date()) ? formatTime(value) : formatDateTime(value)
}

/** "às 14:32" se for hoje, senão "em 21/10/2026 14:32". */
export function formatWhen(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return sameLocalDay(d, new Date()) ? `às ${formatTime(value)}` : `em ${formatDateTime(value)}`
}

/** 30 → "30 min", 90 → "1 h 30 min", 480 → "8 h". */
export function formatMinutes(minutes) {
  const m = Math.round(Number(minutes))
  if (!Number.isFinite(m) || m < 0) return ''
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const rest = m % 60
  return rest ? `${h} h ${rest} min` : `${h} h`
}

export const isKioskSuspended = (s) =>
  s?.kioskState === 'suspended' || (s?.kioskState == null && isFuture(s?.kioskSuspendedUntil))

export const isMdmPaused = (s) => s?.kioskState === 'paused' || isFuture(s?.mdmPausedUntil)

const untilSuffix = (value) => (value ? ` até ${formatUntil(value)}` : '')

/**
 * Situação local do tablet (agente ≥ 1.1, CONTRACT.md §7/§12) como selos:
 * { key, label, short, className, title }. Vale para FleetItemDto e MdmStateDto.
 */
export function localStateBadges(s) {
  if (!s) return []
  const out = []
  if (s.localRemovedAt) {
    out.push({
      key: 'removed',
      label: `MDM removido no tablet em ${formatDateTime(s.localRemovedAt)}`,
      short: 'MDM removido',
      className: 'bg-red-100 text-red-800',
      title: 'O técnico removeu o gerenciamento no próprio tablet. Para voltar a gerenciar, cadastre de novo pelo cabo USB.',
    })
  }
  // Heartbeat já chegou (lastSeenAt) e o agente não é Device Owner: o MDM não manda de verdade no tablet.
  if (!s.localRemovedAt && s.isDeviceOwner === false && s.lastSeenAt) {
    out.push({
      key: 'noOwner',
      label: 'Sem Device Owner',
      short: 'Sem Device Owner',
      className: 'bg-red-100 text-red-800',
      title:
        'O agente foi instalado como app comum: atualizações pedem confirmação no tablet e quiosque/restrições não valem. ' +
        'Remova as contas do tablet e rode pelo cabo: adb shell dpm set-device-owner io.automais.mdm/.AdminReceiver',
    })
  }
  if (isMdmPaused(s)) {
    out.push({
      key: 'paused',
      label: `MDM pausado${untilSuffix(s.mdmPausedUntil)}`,
      short: 'MDM pausado',
      className: 'bg-amber-100 text-amber-800',
      title: 'Quiosque e restrições de uso suspensos pelo técnico. VPN, localização e comandos continuam.',
    })
  }
  if (isKioskSuspended(s)) {
    out.push({
      key: 'suspended',
      label: `Fora do quiosque${untilSuffix(s.kioskSuspendedUntil)}`,
      short: 'Fora do quiosque',
      className: 'bg-amber-100 text-amber-800',
      title: 'O técnico saiu do quiosque por um tempo. O tablet volta sozinho no fim do prazo.',
    })
  }
  if (s.kioskState === 'crashloop') {
    out.push({
      key: 'crashloop',
      label: 'App do quiosque travando',
      short: 'App travando',
      className: 'bg-red-100 text-red-800',
      title: 'O app do quiosque fechou várias vezes seguidas. Veja o Histórico do tablet.',
    })
  }
  if (s.kioskState === 'updating') {
    out.push({
      key: 'updating',
      label: 'Atualizando o app do quiosque',
      short: 'Atualizando app',
      className: 'bg-blue-100 text-blue-800',
      title: 'O quiosque volta sozinho quando a instalação terminar.',
    })
  }
  if (s.localAdminActive) {
    out.push({
      key: 'admin',
      label: 'Técnico usando o tablet',
      short: 'Técnico no tablet',
      className: 'bg-amber-100 text-amber-800',
      title: 'Modo técnico aberto no tablet (último contato).',
    })
  }
  if (s.vpnEnabled === false) {
    out.push({
      key: 'vpn-off',
      label: 'VPN desligada',
      short: 'VPN desligada',
      className: 'bg-gray-100 text-gray-600',
      title: 'A política deste tablet desliga a VPN.',
    })
  }
  return out
}

/** Campos do FieldsModal para o comando `message` (CONTRACT.md §6). */
export const MESSAGE_FIELDS = [
  { key: 'title', label: 'Título', placeholder: 'Aviso', required: true },
  { key: 'text', label: 'Texto', type: 'textarea', required: true },
  { key: 'high', label: 'Prioridade alta', type: 'checkbox', hint: 'A mensagem aparece em destaque no tablet.' },
  {
    key: 'requireAck',
    label: 'Pedir confirmação de leitura (Ciente)',
    type: 'checkbox',
    hint: 'A mensagem só sai da tela quando a pessoa tocar em "Ciente"; o painel mostra a hora da leitura.',
  },
]

/** Payload do `message` a partir dos valores do formulário. */
export function messagePayload({ title, text, high, requireAck }) {
  return {
    title: String(title || '').trim(),
    text: String(text || '').trim(),
    priority: high ? 'high' : 'normal',
    requireAck: !!requireAck,
  }
}

/** "Lido às 14:32" / "Exibido às 14:30 · aguardando Ciente" a partir do resultado do `message`. */
export function messageReadLabel(command) {
  const r = command?.result || {}
  if (r.readAt) return { text: `Lido ${formatWhen(r.readAt)}`, ok: true }
  if (r.shownAt) {
    const waiting = command?.payload?.requireAck ? ' · aguardando "Ciente"' : ''
    return { text: `Exibido ${formatWhen(r.shownAt)}${waiting}`, ok: false }
  }
  return null
}
